/**
 * CustomFieldValueInput
 *
 * Renders the right input control for a custom field's value depending on
 * the field's type:
 *   - Text   → single-line TextField
 *   - Number → numeric TextField
 *   - Select → multi-select Autocomplete over the field's curated options,
 *              with type-to-add. A newly typed option is persisted back to
 *              the field definition (via `onAddOption`) so it appears in
 *              every other course's picker for the same field.
 *
 * Values are always `string[]` (the API shape). Text/Number hold at most
 * one element; Select holds the chosen options.
 */

import { useMemo, useState } from 'react';
import { CourseCustomField, CourseCustomFieldType } from '../../apis/api-client';
import { Autocomplete, Box, Checkbox, Chip, TextField, Typography } from '../ui';

interface CustomFieldValueInputProps {
  field: CourseCustomField;
  value: string[];
  onChange: (next: string[]) => void;
  /** Persist a newly typed option onto the field definition (Select only). */
  onAddOption?: (option: string) => void;
}

type SelectOption = string | { __new: true; name: string };

export default function CustomFieldValueInput({
  field,
  value,
  onChange,
  onAddOption,
}: CustomFieldValueInputProps) {
  // Hooks must run unconditionally — the select-only input state sits idle
  // for text/number fields, which is fine.
  const [inputValue, setInputValue] = useState('');

  const options = field.options ?? [];

  const dropdownOptions = useMemo<SelectOption[]>(() => {
    const q = inputValue.trim().toLowerCase();
    const matches = q ? options.filter((o) => o.toLowerCase().includes(q)) : options;
    const exact = q && options.some((o) => o.toLowerCase() === q);
    if (q && !exact) return [...matches, { __new: true, name: inputValue.trim() }];
    return matches;
  }, [options, inputValue]);

  // ── Number — single numeric value ───────────────────────────────────
  if (field.fieldType === CourseCustomFieldType.Number) {
    return (
      <TextField
        label={field.name}
        type="number"
        value={value[0] ?? ''}
        onChange={(e) => onChange(e.target.value === '' ? [] : [e.target.value])}
        helperText="Numeric value"
        inputProps={{ step: '0.25', min: 0 }}
        size="small"
      />
    );
  }

  // ── Text — single free-text value ───────────────────────────────────
  if (field.fieldType !== CourseCustomFieldType.Select) {
    return (
      <TextField
        label={field.name}
        value={value[0] ?? ''}
        onChange={(e) => onChange(e.target.value === '' ? [] : [e.target.value])}
        size="small"
      />
    );
  }

  // ── Select — multi-value from curated options ───────────────────────
  const selected = value;
  return (
    <Autocomplete<SelectOption, true, false, true>
      multiple
      freeSolo
      disableCloseOnSelect
      size="small"
      options={dropdownOptions}
      value={selected as any}
      onChange={(_, next) => {
        const cleaned: string[] = [];
        for (const item of next) {
          if (typeof item === 'string') {
            // freeSolo returns a raw string when the user hits Enter on
            // text that doesn't match an existing option.
            const trimmed = item.trim();
            if (!trimmed) continue;
            const matchExisting = options.find((o) => o.toLowerCase() === trimmed.toLowerCase());
            if (matchExisting) {
              if (!cleaned.includes(matchExisting)) cleaned.push(matchExisting);
            } else {
              onAddOption?.(trimmed);
              if (!cleaned.includes(trimmed)) cleaned.push(trimmed);
            }
          } else if ((item as { __new?: boolean }).__new) {
            const newName = (item as { name: string }).name;
            onAddOption?.(newName);
            if (!cleaned.includes(newName)) cleaned.push(newName);
          }
        }
        onChange(cleaned);
      }}
      inputValue={inputValue}
      onInputChange={(_, v) => setInputValue(v)}
      filterOptions={(x) => x}
      getOptionLabel={(opt) => (typeof opt === 'string' ? opt : opt.name)}
      isOptionEqualToValue={(opt, val) => {
        if (typeof opt === 'string' && typeof val === 'string') return opt === val;
        return false;
      }}
      renderOption={(props, option) => {
        if (typeof option !== 'string' && (option as { __new?: boolean }).__new) {
          return (
            <Box
              component="li"
              {...props}
              key="__new__"
              sx={{ display: 'flex !important', justifyContent: 'space-between' }}
            >
              <Typography variant="body2">
                Add option <strong>"{(option as { name: string }).name}"</strong>
              </Typography>
              <Typography variant="caption" color="text.secondary">New</Typography>
            </Box>
          );
        }
        const s = option as string;
        return (
          <Box component="li" {...props} key={s}>
            <Checkbox checked={selected.includes(s)} size="small" sx={{ mr: 1, p: 0.5 }} />
            {s}
          </Box>
        );
      }}
      renderTags={(items, getTagProps) =>
        items.map((option, index) => (
          <Chip
            label={typeof option === 'string' ? option : option.name}
            size="small"
            {...getTagProps({ index })}
            key={String(option) + index}
          />
        ))
      }
      renderInput={(params) => (
        <TextField
          {...params}
          label={field.name}
          placeholder={
            selected.length === 0
              ? options.length > 0
                ? 'Choose or add values'
                : 'Type to add values'
              : ''
          }
          helperText={
            options.length > 0
              ? `Multi-select · ${options.length} option${options.length === 1 ? '' : 's'} · type to find or add`
              : 'Multi-select · type to add options'
          }
        />
      )}
    />
  );
}
